/** Pure spatial DSL descriptor collection; execution stays in the host. */
import type { InspectorOptions } from './native-inspector';
import { bevelRadiusUnitWarning, fontSizeUnitWarning, legacyBevelRadiusMetres } from './unit-guards';
import { mergeMaterialSpecs, normalizeBaseColor, normalizeMaterialSpec, unboundColorNote } from './spatial-material';
import { describeEnvironment, resolveEnvironment, resolveKeyLight, type SpatialEnvironmentDescriptor, type EnvironmentPresetUrls, } from './spatial-environment';
import type { BehaviorData, AnimationData, AnimationSequenceData, AutoUIData, BevelData, BillboardData, BoxColliderData, ContentSlotData, FrameData, ImageData as TraceImageData, LayoutData, LayoutItemData, MoveableData, PagesData, ParticleSystemData, PropertiesData, RendererData, RotatableData, RotateData, SphereColliderData, SynthData, TextData, VideoData, } from '../../exports/behaviors';
import type { SpatialTemplate, SpatialObjectDescriptor, SpatialInteractionDescriptor, SpatialBuildStats, SpatialBuildResult, SpatialSceneSnapshot } from './spatial-types';
import { SCENE_OBJECT_BUDGET } from './spatial-types';
import { BUTTON_STATES, createBlockTools } from './block-tools';
import { buildCallouts, type CalloutItem, type CalloutOptions } from './spatial-callouts';
import { validateSpatialInteractions } from './interaction-input';
import { finishBuild } from './finish';
import { readVec3, SandboxVec3, SandboxMathUtil, createRng } from './spatial-math';
import { SEAT_DIRECTIONS, type SeatDirection } from '../fit/sweep';
/** Descriptors are JSON by contract. A QuickJS host defines no structuredClone, so the collector copies through JSON instead. */
const jsonCopy = <T>(value: T): T => (value === undefined ? value : JSON.parse(JSON.stringify(value)) as T);
const INSTANCE_SHAPES = ['cube', 'sphere', 'cylinder', 'plane'] as const;
const SPATIAL_BEHAVIOR_TYPES = [
    'Rotate', 'Billboard', 'BoxCollider', 'SphereCollider',
    'Text', 'Properties',
    'ParticleSystem', 'Animation', 'AnimationSequence', 'Frame', 'Image', 'Layout', 'LayoutItem',
    'Moveable', 'Rotatable', 'Synth', 'Video', 'AutoUI', 'Renderer', 'Bevel', 'ContentSlot', 'Pages',
] as const;
export type SpatialBehaviorType = typeof SPATIAL_BEHAVIOR_TYPES[number];
interface SpatialBehaviorDataMap extends Record<SpatialBehaviorType, BehaviorData> {
    Animation: AnimationData;
    AnimationSequence: AnimationSequenceData;
    AutoUI: AutoUIData;
    Bevel: BevelData;
    ContentSlot: ContentSlotData;
    Billboard: BillboardData;
    BoxCollider: BoxColliderData;
    Frame: FrameData;
    Image: TraceImageData;
    Layout: LayoutData;
    LayoutItem: LayoutItemData;
    Moveable: MoveableData;
    Pages: PagesData;
    ParticleSystem: ParticleSystemData;
    Properties: PropertiesData;
    Renderer: RendererData;
    Rotatable: RotatableData;
    Rotate: RotateData;
    SphereCollider: SphereColliderData;
    Synth: SynthData;
    Text: TextData;
    Video: VideoData;
}
type SpatialOverrides<K extends SpatialBehaviorType> = Partial<Omit<SpatialBehaviorDataMap[K], '@type'>>;
const ALLOWED_BEHAVIOR_TYPES: ReadonlySet<string> = new Set(SPATIAL_BEHAVIOR_TYPES);
export interface SpatialExecuteOptions {
    budget?: number;
    existingCount?: number;
    /** Host-owned optional environment URLs; none and neutral use engine defaults. */
    environmentPresets?: EnvironmentPresetUrls;
    /**
     * The tool surface the host offers a model. `blocks` is the layout surface: unit, stack, panel, row, text, image, button, chip,
     * facts and stage3d, with layout validated in `result()`; the free-placement helpers (makeCard, makeButton, card, background,
     * label, and the free text/stack/image) are not offered. `compat` is every helper, as before, and is the default until hosts move.
     */
    surface?: 'blocks' | 'compat';
    /**
     * The picked Style's values (`titleFont`, `bodyScale`, `cardColor`, ...). On the blocks surface its typography sets the type ramp and its
     * colours and fonts are what the unit declares until the Style is applied, so the finish pass judges contrast on the real palette.
     */
    style?: Readonly<Record<string, unknown>>;
    /** Hosted font families the deployment declares, beyond the bundled ones: `html()` sets a page's `font-family` in them when it names one. */
    fonts?: readonly string[];
}
export interface SpatialBuildSession {
    api: Record<string, unknown>;
    result(errors?: string[]): SpatialBuildResult;
}
/**
 * A Properties pose's rotation is a quaternion `[x, y, z, w]`. Three values
 * were read as one: `["{30 * t}", 0, 0]` became `[30 t, 0, 0, 1]`, not 30 t degrees.
 */
function assertQuaternionRotations(bag: Record<string, unknown>, name: string): void {
    type Pose = { rotation?: unknown } | undefined;
    const states = (bag.states ?? {}) as Record<string, { transform?: Pose; enterFrom?: Pose }>;
    const poses: [string, Pose][] = [['transform', bag.transform as Pose],
        ...Object.entries(states).flatMap(([state, def]): [string, Pose][] => [[`states.${state}.transform`, def?.transform], [`states.${state}.enterFrom`, def?.enterFrom]])];
    for (const [where, pose] of poses) {
        const rotation = pose?.rotation;
        if (rotation === undefined || (Array.isArray(rotation) && rotation.length === 4)) continue;
        throw new Error(`addBehavior("${name}", "Properties"): ${where}.rotation must be a quaternion [x, y, z, w], `
            + `got ${JSON.stringify(rotation)}. A turn of a radians about X is ["{sin(a / 2)}", 0, 0, "{cos(a / 2)}"].`);
    }
}

/** The step badge's fill: the makeButton blue, so a deck's accents match. */
const STEP_BADGE_COLOR = '#2563EB';

/** `#RGB`, `#RRGGBB` or `#RRGGBBAA` as its channels, alpha last when given; null for anything else (a binding). */
function hexChannels(color?: string): number[] | null {
    const m = /^#([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i.exec(color?.trim() ?? '');
    if (!m) return null;
    const hex = m[1].length === 3 ? m[1].replace(/./g, '$&$&') : m[1];
    return Array.from({ length: hex.length / 2 }, (_, i) => parseInt(hex.slice(2 * i, 2 * i + 2), 16));
}

const toHex = (rgb: number[]) => '#' + rgb.slice(0, 3).map(c => Math.round(c).toString(16).padStart(2, '0')).join('').toUpperCase();

/**
 * A saturated, dark-enough label colour (a red caution) is its badge's colour
 * too, so the white number reads on it; any other colour gets the blue.
 */
function badgeColor(text?: string): string {
    const rgb = hexChannels(text)?.slice(0, 3);
    const luma = rgb ? 0.299 * rgb[0] + 0.587 * rgb[1] + 0.114 * rgb[2] : 255;
    return rgb && Math.max(...rgb) - Math.min(...rgb) > 64 && luma < 160 ? toHex(rgb) : STEP_BADGE_COLOR;
}

/**
 * The emissive a UI surface (a card body, a step badge) carries: 30% of its own
 * colour. Upright surfaces facing the camera catch little of the key light, so
 * a white card rendered grey-beige; the lift keeps it near its colour, while
 * the lit share still shades its rounded rim. Null for a binding or a
 * translucent colour, which shows what is behind it.
 */
function surfaceLift(color: string): string | null {
    const rgb = hexChannels(color);
    return rgb && (rgb[3] ?? 255) === 255 ? toHex(rgb.map(c => 0.3 * c)) : null;
}

export function createSpatialBuild(seed?: number, existingScene?: SpatialSceneSnapshot[], options?: SpatialExecuteOptions): SpatialBuildSession {
    const budget = options?.budget ?? SCENE_OBJECT_BUDGET;
    const existingCount = options?.existingCount ?? existingScene?.length ?? 0;
    let instanceCalls = 0;
    let instanceObjects = 0;
    const objects: SpatialObjectDescriptor[] = [];
    const warnings: string[] = [];
    let idCounter = 0;
    const rng = createRng(seed ?? 42);
    /** The objects inside a baked group: they fold into its mesh and are never created. */
    function folded(): Set<string> {
        const byId = new Map(objects.map(o => [o.id, o]));
        const inBake = new Map<string, boolean>();
        const ids = new Set<string>();
        for (const object of objects) {
            const chain: SpatialObjectDescriptor[] = [];
            let result = false;
            for (let at: SpatialObjectDescriptor | undefined = object; at;) {
                const known = inBake.get(at.id);
                if (known !== undefined) {
                    result = known;
                    break;
                }
                if (chain.includes(at))
                    break;
                chain.push(at);
                const parent: SpatialObjectDescriptor | undefined = at.parentId ? byId.get(at.parentId) : undefined;
                if (parent?.bake) {
                    result = true;
                    break;
                }
                at = parent;
            }
            for (const seen of chain)
                inBake.set(seen.id, result);
            if (inBake.get(object.id))
                ids.add(object.id);
        }
        return ids;
    }
    function statsNow(): SpatialBuildStats {
        const created = objects.length - folded().size;
        return {
            created,
            existing: existingCount,
            budget: Number.isFinite(budget) ? budget : null,
            remaining: Number.isFinite(budget) ? Math.max(0, budget - existingCount - created) : null,
            instanceCalls,
            instanceObjects,
        };
    }
    function reserve(count: number): void {
        if (existingCount + objects.length + count <= budget)
            return;
        const total = existingCount + objects.length - folded().size + count;
        if (total <= budget)
            return;
        throw new Error(`Scene budget exceeded: this build would take the scene to ${total} objects, `
            + `over the budget of ${budget} (${existingCount} already in the scene, `
            + `${objects.length} created by this build so far). Nothing was created. `
            + 'The budget is for STRUCTURE, not surface: put repeated elements through '
            + '`instances(def, transforms)` (one call, N objects) and put texture into '
            + '`material()` — colour, roughness, metallic, emissive — rather than one '
            + 'object per cobble, tile or speckle. Detail concentrated where it carries '
            + 'the shape scores better than an even field of it.');
    }
    /** Whether a name or path names an object that was in the scene before this build. */
    function inScene(ref: string): boolean {
        return !!existingScene?.some(o => o.name === ref || o.path === ref || o.path?.endsWith(`/${ref}`));
    }
    /**
     * A handle, or the name of exactly one object this build created. A name
     * the scene already has stays the scene's, as it was before names resolved
     * here, so the calls that take existing objects (parent, clone) still reach it.
     */
    function findObj(ref: string): SpatialObjectDescriptor | undefined {
        const byId = objects.find(o => o.id === ref);
        if (byId || typeof ref !== 'string' || ref.startsWith('__sp_') || inScene(ref))
            return byId;
        // A name given with "/" still finds the object, which has "-" instead.
        const named = objects.filter(o => o.name === ref || o.name === pathSafe(ref, false));
        if (named.length > 1)
            throw new Error(`"${ref}" names ${named.length} objects in this build; use the handle the creating call returned.`);
        return named[0];
    }
    /** Names given with "/", which separates path segments: each object gets "-" instead. */
    const slashed = new Set<string>();
    function pathSafe(name: string, given = true): string {
        if (typeof name !== 'string' || !name.includes('/'))
            return name;
        if (given)
            slashed.add(name);
        return name.replaceAll('/', '-');
    }
    /** `base`, or `base 2`, `base 3`… when this build or the scene already has that name. */
    function freeName(base: string): string {
        const taken = (name: string) => inScene(name) || objects.some(o => o.name === name);
        let name = base;
        for (let i = 2; taken(name); i++) name = `${base} ${i}`;
        return name;
    }
    /** Objects named by the default of an unnamed instances() call ("clone 1"), which nothing calls by name. */
    const unnamed = new Set<string>();
    /**
     * Names this build gives that another object has too, in the scene or in
     * this build, so every name-based call on them is ambiguous. A copy named
     * like its prototype is the common case.
     */
    function nameCollisionWarning(): string | undefined {
        const skip = folded();
        const counts = new Map<string, number>();
        for (const o of objects) {
            if (!skip.has(o.id) && !unnamed.has(o.id))
                counts.set(o.name, (counts.get(o.name) ?? 0) + 1);
        }
        const shared = [...counts].filter(([name, n]) => n > 1 || existingScene?.some(o => o.name === name))
            .map(([name, n]) => `"${name}" (${n > 1 ? `${n} in this build` : 'also in the scene'})`);
        if (!shared.length)
            return undefined;
        const listed = shared.length > 8 ? [...shared.slice(0, 8), `and ${shared.length - 8} more`] : shared;
        return `Names another object has too: ${listed.join(', ')}. Name-based calls on them (parent, clone, set_transform) are ambiguous; name each apart (e.g. "Bolt 4", "Seat Back").`;
    }
    /** The handle for a handle or a name of this build's, else the ref as given (an existing object). */
    function idOf(ref: string): string {
        return findObj(ref)?.id ?? ref;
    }
    let environment: SpatialEnvironmentDescriptor | undefined;
    function createObject(template: SpatialTemplate, name?: string): string {
        reserve(1);
        const id = `__sp_${idCounter++}`;
        const displayName = name !== undefined ? pathSafe(name) : `${template}_${idCounter}`;
        objects.push({ id, name: displayName, template });
        return id;
    }
    function setMaterial(obj: string, opts: unknown): void {
        const desc = findObj(obj);
        if (!desc)
            throw new Error(`material: unknown object handle "${obj}"`);
        const { spec, warnings: specWarnings } = normalizeMaterialSpec(opts, desc.name);
        warnings.push(...specWarnings);
        desc.material = mergeMaterialSpecs(desc.material, spec);
        if (spec.color !== undefined)
            desc.color = spec.color;
    }
    function createPrimitive(template: SpatialTemplate, name?: string, opts?: unknown): string {
        const id = createObject(template, name);
        if (opts !== undefined)
            setMaterial(id, opts);
        return id;
    }
    function createAsset(assetId: string, name?: string): string {
        reserve(1);
        if (!assetId || typeof assetId !== 'string') {
            throw new Error('asset() requires a catalog asset ID (string). Use search_catalog to find IDs first.');
        }
        const id = `__sp_${idCounter++}`;
        const displayName = name !== undefined ? pathSafe(name) : `asset_${idCounter}`;
        objects.push({ id, name: displayName, template: 'asset', assetId });
        return id;
    }
    /**
     * The group this build bakes that a clone names, which the clone copies here;
     * undefined for an object already in the scene. Any other source of this
     * build can't be copied yet: a clone copies an object that exists. With no
     * scene snapshot to tell them apart, a source that isn't baked here is left
     * to the materializer, as before.
     */
    function bakedCloneSource(sourceName: string, call: string): SpatialObjectDescriptor | undefined {
        if (inScene(sourceName))
            return undefined;
        const desc = findObj(sourceName);
        if (!desc || desc.bake)
            return desc;
        if (!existingScene)
            return undefined;
        throw new Error(`${call}: "${desc.name}" is created by this build, and a clone copies an object that already exists. `
            + 'bake() it first to copy it in this build, or build it in an earlier call.');
    }
    /** Copies of groups this build bakes; they take the group's parts when the build ends. */
    const bakedCopies: { source: SpatialObjectDescriptor; copy: SpatialObjectDescriptor }[] = [];
    /** Make `copy` a baked group that takes `source`'s parts when the build ends, so both bake to one shared mesh. */
    function copyBakedParts(source: SpatialObjectDescriptor, copy: SpatialObjectDescriptor): void {
        copy.template = 'empty';
        copy.bake = true;
        delete copy.sourceObjectName;
        bakedCopies.push({ source, copy });
    }
    /** Give each baked copy its source's final parts; a recoloured copy is an error, since a bake keeps its parts' colours. */
    function finishBakedCopies(): string[] {
        const errors: string[] = [];
        for (const { source, copy } of bakedCopies.splice(0)) {
            if (copy.color !== undefined || copy.material !== undefined) {
                errors.push(`"${copy.name}" copies the baked group "${source.name}", which keeps its parts' colours; bake a recoloured group to change them.`);
                continue;
            }
            copy.rotation ??= source.rotation;
            copy.scale ??= source.scale;
            // The group's own colour reaches its uncoloured parts in the bake, so the copy carries it too.
            copy.color = source.color;
            copy.material = jsonCopy(source.material);
            const walk = (from: string, to: string): void => {
                for (const child of objects.filter(o => o.parentId === from)) {
                    const id = `__sp_${idCounter++}`;
                    objects.push({ ...jsonCopy(child), id, parentId: to });
                    walk(child.id, id);
                }
            };
            walk(source.id, copy.id);
        }
        return errors;
    }
    function cloneObject(sourceName: string, name?: string): string {
        reserve(1);
        if (!sourceName || typeof sourceName !== 'string') {
            throw new Error('clone() requires the name of an existing scene object.');
        }
        const baked = bakedCloneSource(sourceName, 'clone()');
        const id = `__sp_${idCounter++}`;
        const displayName = name !== undefined ? pathSafe(name) : pathSafe(`${baked?.name ?? sourceName}_${idCounter}`, false);
        const desc: SpatialObjectDescriptor = { id, name: displayName, template: 'clone', sourceObjectName: sourceName };
        objects.push(desc);
        if (baked)
            copyBakedParts(baked, desc);
        return id;
    }
    function createInstances(def: unknown, transforms: unknown, groupName?: string): string[] {
        if (def === null || typeof def !== 'object' || Array.isArray(def)) {
            throw new Error('instances(def, transforms): `def` must be an options object, e.g. '
                + '{ shape: "cylinder", name: "Column", scale: [0.4, 4, 0.4], material: { color: "#C9C2B4", roughness: 0.8 } }.');
        }
        if (!Array.isArray(transforms) || transforms.length === 0) {
            throw new Error('instances(def, transforms): `transforms` must be a non-empty array of [x,y,z] or {position, rotation, scale}.');
        }
        const entries = transforms as unknown[];
        const bag = def as Record<string, unknown>;
        const allowed = ['shape', 'asset', 'clone', 'name', 'material', 'color', 'scale', 'rotation', 'parent'];
        const unknownKeys = Object.keys(bag).filter(k => !allowed.includes(k));
        if (unknownKeys.length > 0) {
            throw new Error(`instances(): unknown option(s) ${unknownKeys.map(k => `"${k}"`).join(', ')}. Allowed: ${allowed.join(', ')}.`);
        }
        const sources = ['shape', 'asset', 'clone'].filter(k => bag[k] !== undefined);
        if (sources.length !== 1) {
            throw new Error('instances(): give exactly ONE of shape ("cube"|"sphere"|"cylinder"|"plane"), '
                + 'asset (a catalog ID) or clone (an existing object name).');
        }
        let template: SpatialTemplate;
        let bakedSource: SpatialObjectDescriptor | undefined;
        let assetId: string | undefined;
        let sourceObjectName: string | undefined;
        if (bag.shape !== undefined) {
            const shape = bag.shape;
            if (typeof shape !== 'string' || !(INSTANCE_SHAPES as readonly string[]).includes(shape)) {
                throw new Error(`instances(): shape must be one of ${INSTANCE_SHAPES.join(', ')}.`);
            }
            template = shape as SpatialTemplate;
        }
        else if (bag.asset !== undefined) {
            if (typeof bag.asset !== 'string' || !bag.asset)
                throw new Error('instances(): asset must be a catalog asset ID (string).');
            template = 'asset';
            assetId = bag.asset;
        }
        else {
            if (typeof bag.clone !== 'string' || !bag.clone)
                throw new Error('instances(): clone must be the name of an existing scene object.');
            bakedSource = bakedCloneSource(bag.clone, 'instances()');
            template = 'clone';
            sourceObjectName = bag.clone;
        }
        const named = typeof bag.name === 'string' && bag.name;
        const baseName = named ? pathSafe(bag.name as string) : groupName ?? template;
        const defScale = bag.scale === undefined ? undefined : readVec3(bag.scale, 'instances(): scale');
        const defRotation = bag.rotation === undefined ? undefined : readVec3(bag.rotation, 'instances(): rotation');
        let parentTarget: string | undefined;
        if (bag.parent !== undefined) {
            if (typeof bag.parent !== 'string') {
                throw new Error('instances(): parent must be a handle from this build or the name/path of an existing scene object.');
            }
            parentTarget = bag.parent;
        }
        reserve(entries.length);
        const group = `__inst_${instanceCalls++}`;
        instanceObjects += entries.length;
        const handles: string[] = [];
        for (let i = 0; i < entries.length; i++) {
            const entry = entries[i];
            let position: [
                number,
                number,
                number
            ] | undefined;
            let rotation = defRotation;
            let scaleVec = defScale;
            if (Array.isArray(entry)) {
                position = readVec3(entry, `instances(): transforms[${i}]`);
            }
            else if (entry !== null && typeof entry === 'object') {
                const t = entry as Record<string, unknown>;
                const tKeys = Object.keys(t).filter(k => !['position', 'rotation', 'scale', 'name'].includes(k));
                if (tKeys.length > 0) {
                    throw new Error(`instances(): transforms[${i}] has unknown key(s) ${tKeys.map(k => `"${k}"`).join(', ')}. `
                        + 'Allowed: position, rotation, scale, name.');
                }
                if (t.position !== undefined)
                    position = readVec3(t.position, `instances(): transforms[${i}].position`);
                if (t.rotation !== undefined)
                    rotation = readVec3(t.rotation, `instances(): transforms[${i}].rotation`);
                if (t.scale !== undefined)
                    scaleVec = readVec3(t.scale, `instances(): transforms[${i}].scale`);
            }
            else {
                throw new Error(`instances(): transforms[${i}] must be [x,y,z] or {position, rotation, scale}.`);
            }
            const id = `__sp_${idCounter++}`;
            const given = Array.isArray(entry) ? undefined : (entry as Record<string, unknown>).name;
            if (given !== undefined && typeof given !== 'string') {
                throw new Error(`instances(): transforms[${i}].name must be a string.`);
            }
            if (given === undefined && !named && groupName === undefined)
                unnamed.add(id);
            const desc: SpatialObjectDescriptor = {
                id,
                name: given !== undefined ? pathSafe(given) : (entries.length === 1 ? baseName : `${baseName} ${i + 1}`),
                template,
                instanceGroup: group,
                ...(assetId !== undefined ? { assetId } : {}),
                ...(sourceObjectName !== undefined ? { sourceObjectName } : {}),
                ...(position !== undefined ? { position } : {}),
                ...(rotation !== undefined ? { rotation } : {}),
                ...(scaleVec !== undefined ? { scale: scaleVec } : {}),
            };
            objects.push(desc);
            if (bakedSource)
                copyBakedParts(bakedSource, desc);
            if (bag.color !== undefined)
                setColor(id, bag.color);
            if (bag.material !== undefined)
                setMaterial(id, bag.material);
            if (parentTarget !== undefined)
                setParent(id, parentTarget);
            handles.push(id);
        }
        return handles;
    }
    function move(obj: string, x: number, y: number, z: number): void {
        const desc = findObj(obj);
        if (!desc)
            throw new Error(`move: unknown object handle "${obj}"`);
        desc.position = [x, y, z];
    }
    function rotate(obj: string, x: number, y: number, z: number): void {
        const desc = findObj(obj);
        if (!desc)
            throw new Error(`rotate: unknown object handle "${obj}"`);
        desc.rotation = [x, y, z];
    }
    function setScale(obj: string, x: number, y: number, z: number): void {
        const desc = findObj(obj);
        if (!desc)
            throw new Error(`scale: unknown object handle "${obj}"`);
        desc.scale = [x, y, z];
    }
    function setColor(obj: string, hex: unknown): void {
        const desc = findObj(obj);
        if (!desc)
            throw new Error(`color: unknown object handle "${obj}"`);
        const normalized = typeof hex === 'string' ? normalizeBaseColor(hex) : null;
        if (normalized === null) {
            warnings.push(`color("${desc.name}"): "${String(hex)}" is not #RRGGBB or #RRGGBBAA — using the default grey.${unboundColorNote(hex)}`);
        }
        desc.color = normalized ?? (hex as string);
        if (desc.material?.color !== undefined && normalized !== null)
            desc.material.color = normalized;
    }
    function setEnvironment(preset: unknown, opts?: unknown): void {
        const resolved = resolveEnvironment(preset, opts, options?.environmentPresets);
        environment = { ...resolved, ...(environment?.keyLight ? { keyLight: environment.keyLight } : {}) };
        if (!resolved.benchSafe) {
            warnings.push(`environment("${resolved.preset}"): only "neutral" and "none" are known to resolve in the `
                + `offline capture page — the .env cube maps fail silently there and the scene renders `
                + `unlit. Prefer "neutral" and set the mood with material() and light().`);
        }
    }
    function setLight(opts: unknown): void {
        const keyLight = resolveKeyLight(opts);
        environment = { ...(environment ?? resolveEnvironment('neutral')), keyLight };
    }
    const parentRedirects = new Map<string, string>();
    /** A step label's group → its text child, where Text and LayoutItem calls on the label land. */
    const textRedirects = new Map<string, string>();
    function setParent(child: string, requested: string): void {
        const parentObj = parentRedirects.get(requested) ?? requested;
        const childDesc = findObj(child);
        if (!childDesc)
            throw new Error(`parent: unknown child handle "${child}"`);
        const parentDesc = findObj(parentObj);
        if (parentDesc) {
            childDesc.parentId = parentDesc.id;
            childDesc.parentObjectName = undefined;
            return;
        }
        const exact = existingScene?.find(o => o.path === parentObj)
            ?? existingScene?.find(o => o.path === `Scene/${parentObj}`);
        const named = exact ? [] : existingScene?.filter(o => o.path?.endsWith(`/${parentObj}`) || o.name === parentObj) ?? [];
        if (named.length > 1) {
            throw new Error(`parent: "${parentObj}" names ${named.length} scene objects (${named.map(o => o.path ?? o.name).join(', ')}); `
                + 'use a path.');
        }
        const existing = exact ?? named[0];
        if (existing) {
            childDesc.parentObjectName = existing.path ?? existing.name;
            childDesc.parentId = undefined;
            return;
        }
        throw new Error(`parent: unknown parent "${parentObj}" — not a handle or name from this build, ` +
            'and no scene object has that name. Use listObjects() to see existing objects.');
    }
    function addBehavior<K extends SpatialBehaviorType>(obj: string, type: K, overrides?: SpatialOverrides<K>): void {
        const redirected = type === 'Text' || type === 'LayoutItem' ? textRedirects.get(idOf(obj)) : undefined;
        const desc = findObj(redirected ?? obj);
        if (!desc)
            throw new Error(`addBehavior: unknown object handle "${obj}"`);
        if (!ALLOWED_BEHAVIOR_TYPES.has(type)) {
            throw new Error(`addBehavior: "${type}" not available in spatial builds. Allowed: ${Array.from(ALLOWED_BEHAVIOR_TYPES).join(', ')}`);
        }
        desc.behaviors ??= [];
        const bag = overrides as Record<string, unknown> | undefined;
        if (type === 'Text' && bag) {
            const w = fontSizeUnitWarning(bag.fontSize, desc.name);
            if (w)
                warnings.push(w);
        }
        if (type === 'Properties' && bag)
            assertQuaternionRotations(bag, desc.name);
        if (type === 'Bevel' && bag && bag.radius !== undefined) {
            const legacy = bag.radius;
            delete bag.radius;
            warnings.push(bevelRadiusUnitWarning(desc.name));
            const metres = legacyBevelRadiusMetres(legacy);
            if (metres !== null && bag.cornerRadius === undefined)
                bag.cornerRadius = metres;
        }
        const existing = desc.behaviors.find(b => b.type === type);
        if (existing) {
            if (bag) {
                existing.overrides = { ...existing.overrides, ...bag };
            }
            return;
        }
        desc.behaviors.push({ type, overrides: bag });
    }
    function bake(obj: string): string {
        const desc = findObj(obj);
        if (!desc)
            throw new Error(`bake: unknown object handle "${obj}"`);
        desc.bake = true;
        return desc.id;
    }
    let seats = 0;
    /** Mark parts to seat once the build exists, in call order; returns what it was given, so it wraps instances(). */
    function seat<T>(parts: T, opts?: unknown): T {
        const list: unknown[] = Array.isArray(parts) ? parts : [parts];
        if (!list.length)
            throw new Error('seat(part | parts[], {dir, on, ignore}): give at least one part.');
        const bag = (opts ?? {}) as Record<string, unknown>;
        if (typeof bag !== 'object' || Array.isArray(bag))
            throw new Error('seat(parts, opts): opts is {dir, on, ignore}.');
        const unknownKeys = Object.keys(bag).filter(k => !['dir', 'on', 'ignore'].includes(k));
        if (unknownKeys.length)
            throw new Error(`seat(): unknown option(s) ${unknownKeys.map(k => `"${k}"`).join(', ')}. Allowed: dir, on, ignore.`);
        if (bag.dir !== undefined && !SEAT_DIRECTIONS.includes(bag.dir as SeatDirection))
            throw new Error(`seat(): dir ${JSON.stringify(bag.dir)} is not one of ${SEAT_DIRECTIONS.join(' ')}.`);
        const refs = (key: 'on' | 'ignore'): string[] | undefined => {
            const v = bag[key];
            if (v === undefined)
                return undefined;
            const all = Array.isArray(v) ? v : [v];
            if (!all.length || all.some(r => typeof r !== 'string' || !r))
                throw new Error(`seat(): ${key} is a part or name, or a list of them.`);
            return (all as string[]).map(idOf);
        };
        const on = refs('on'), ignore = refs('ignore');
        for (const part of list) {
            const desc = typeof part === 'string' ? findObj(part) : undefined;
            if (desc?.seat)
                throw new Error(`seat(): "${desc.name}" is already seated in this build; seat each part once.`);
            if (!desc)
                throw new Error(`seat(): ${JSON.stringify(part)} is not a part this build creates. seat() takes the handles or names of this build's parts; move a part that already exists with set_transform.`);
            desc.seat = { order: seats++, ...(bag.dir !== undefined ? { dir: bag.dir as SeatDirection } : {}), ...(on ? { on } : {}), ...(ignore ? { ignore } : {}) };
        }
        return parts;
    }
    function setActive(obj: string, active: boolean): void {
        const desc = findObj(obj);
        if (!desc)
            throw new Error(`setActive: unknown object handle "${obj}"`);
        desc.active = active;
    }
    function inspector(owner: string, focus: string, options: InspectorOptions = {}): void {
        const desc = findObj(owner);
        if (!desc || !findObj(focus))
            throw new Error('inspector: owner and focus must be handles created in this build');
        if (desc.inspector || desc.behaviors?.some(b => b.type === 'AutoUI'))
            throw new Error('inspector: AutoUI already authored on this owner');
        desc.inspector = { focusId: idOf(focus), options: jsonCopy(options) };
    }
    const interactions: SpatialInteractionDescriptor[] = [];
    const interactionPickModes = new Map<string, 'collider' | 'geometry'>();
    function interaction(sourceRef: string, options: {
        pick?: 'collider' | 'geometry';
        scope?: string;
        when?: string;
    } = {}) {
        if (options.scope !== undefined && !findObj(options.scope))
            throw new Error('interaction: unknown scope handle');
        if (options.when !== undefined && typeof options.when !== 'string')
            throw new Error('interaction: when must be a boolean expression string');
        const pick = options.pick ?? 'collider';
        if (pick !== 'collider' && pick !== 'geometry')
            throw new Error('interaction: pick must be collider or geometry');
        const sourceDesc = findObj(sourceRef);
        if (!sourceDesc)
            throw new Error(`interaction: unknown source handle "${sourceRef}"`);
        const source = sourceDesc.id;
        const scope = options.scope === undefined ? undefined : idOf(options.scope);
        function eventChain(event: NonNullable<SpatialInteractionDescriptor['event']>) {
            if (interactions.length >= 64)
                throw new Error('A build supports at most 64 interactions');
            if (['select', 'hover', 'hoverExit'].includes(event)) {
                sourceDesc!.behaviors ??= [];
                const previous = interactionPickModes.get(source);
                if (previous && previous !== pick)
                    throw new Error('interaction: pointer events on one source must use the same pick mode');
                interactionPickModes.set(source, pick);
                const hasCollider = sourceDesc!.behaviors.some(b => b.type === 'BoxCollider' || b.type === 'SphereCollider');
                if (pick === 'geometry' && hasCollider)
                    throw new Error('interaction: geometry picking requires a source without a collider');
                if (pick === 'collider' && !hasCollider)
                    sourceDesc!.behaviors.push({ type: 'BoxCollider' });
            }
            const actions: Record<string, unknown>[] = [];
            interactions.push({ type: 'native_chain', sourceId: source, targetId: source, event, actions, scopeId: scope, when: options.when });
            const append = (action: string, target: string | undefined, fields: Record<string, unknown> = {}) => {
                if (actions.length >= 32)
                    throw new Error('An event supports at most 32 actions');
                // A target may also be an object already in the scene (a deck a button drives).
                if (target !== undefined && !findObj(target) && !inScene(target))
                    throw new Error(`interaction: unknown target "${target}": not a handle or name from this build, and no scene object has that name`);
                actions.push({ action, ...(target !== undefined ? { target: idOf(target) } : {}), ...jsonCopy(fields) });
                return chain;
            };
            const chain = {
                setPropertyExpr: (target: string, property: string, expression: string, transition?: {
                    duration: number;
                    easing?: string;
                }) => append('setProperty', target, { property, expression, duration: transition?.duration, easing: transition?.easing }),
                setProperty: (target: string, property: string, value: unknown, transition?: {
                    duration: number;
                    easing?: string;
                }) => append('setProperty', target, { property, value, duration: transition?.duration, easing: transition?.easing }),
                setState: (target: string, state = '') => append('setState', target, { state }),
                setGroupState: (target: string, state = '') => append('setGroupState', target, { state }),
                setActive: (target: string, active = true) => append('setActive', target, { active }),
                toggleActive: (target: string) => append('toggleActive', target),
                setActiveChild: (target: string, index: number) => append('setActiveChild', target, { index }),
                playAnimation: (target: string, clip?: string, options?: {
                    loop?: boolean;
                }) => append('playAnimation', target, { clip, loop: options?.loop }),
                playMedia: (target: string) => append('playMedia', target),
                pauseMedia: (target: string) => append('pauseMedia', target),
                pagesNext: (target: string, wrap = true, animate = false) => append('pagesNext', target, { wrap, animate }),
                pagesPrev: (target: string, wrap = true, animate = false) => append('pagesPrev', target, { wrap, animate }),
                navigate: (url: string) => append('navigate', undefined, { url }),
            };
            return chain;
        }
        return {
            onSelect: () => eventChain('select'),
            onHover: () => eventChain('hover'),
            onHoverExit: () => eventChain('hoverExit'),
            onStart: () => eventChain('start'),
            onTick: () => eventChain('tick'),
        };
    }
    function onClickToggle(source: string, target: string): void {
        const sourceDesc = findObj(source);
        if (!sourceDesc)
            throw new Error(`onClickToggle: unknown source handle "${source}"`);
        const targetDesc = findObj(target);
        if (!targetDesc)
            throw new Error(`onClickToggle: unknown target handle "${target}"`);
        sourceDesc.behaviors ??= [];
        if (!sourceDesc.behaviors.find(b => b.type === 'BoxCollider')) {
            sourceDesc.behaviors.push({ type: 'BoxCollider' });
        }
        interactions.push({ type: 'click_toggle', sourceId: sourceDesc.id, targetId: targetDesc.id });
    }
    const exclusiveGroups = new Map<string, Set<string>>();
    function onClickShowExclusive(source: string, target: string, groupName?: string): void {
        const sourceDesc = findObj(source);
        if (!sourceDesc)
            throw new Error(`onClickShowExclusive: unknown source handle "${source}"`);
        const targetDesc = findObj(target);
        if (!targetDesc)
            throw new Error(`onClickShowExclusive: unknown target handle "${target}"`);
        sourceDesc.behaviors ??= [];
        if (!sourceDesc.behaviors.find(b => b.type === 'BoxCollider')) {
            sourceDesc.behaviors.push({ type: 'BoxCollider' });
        }
        const gName = groupName ?? '__excl_default';
        if (!exclusiveGroups.has(gName))
            exclusiveGroups.set(gName, new Set());
        exclusiveGroups.get(gName)!.add(targetDesc.id);
        interactions.push({
            type: 'click_show_exclusive',
            sourceId: sourceDesc.id,
            targetId: targetDesc.id,
            exclusiveGroupIds: [],
        });
    }
    function onClickSetState(source: string, target: string, stateName: string): void {
        const sourceDesc = findObj(source);
        if (!sourceDesc)
            throw new Error(`onClickSetState: unknown source handle "${source}"`);
        const targetDesc = findObj(target);
        if (!targetDesc)
            throw new Error(`onClickSetState: unknown target handle "${target}"`);
        sourceDesc.behaviors ??= [];
        if (!sourceDesc.behaviors.find(b => b.type === 'BoxCollider')) {
            sourceDesc.behaviors.push({ type: 'BoxCollider' });
        }
        interactions.push({ type: 'click_set_state', sourceId: sourceDesc.id, targetId: targetDesc.id, stateName });
    }
    function createParticleEmitter(name?: string, overrides?: SpatialOverrides<'ParticleSystem'>): string {
        const id = createObject('empty', name ?? 'ParticleEmitter');
        addBehavior(id, 'ParticleSystem', overrides);
        return id;
    }
    function createText(name?: string, content?: string, textColor?: string): string {
        const id = createObject('empty', name ?? 'Text');
        const overrides: SpatialOverrides<'Text'> = { fontSize: 10, color: textColor ?? '#FFFFFF' };
        if (content !== undefined)
            overrides.text = content;
        addBehavior(id, 'Text', overrides);
        return id;
    }
    function createCard(name: string, opts?: {
        title?: string;
        body?: string;
        bgColor?: string;
        textColor?: string;
        width?: number;
        height?: number;
    }): string {
        const w = opts?.width ?? 3;
        const h = opts?.height ?? 2;
        const bg = opts?.bgColor ?? '#F5F5F5';
        const tc = opts?.textColor ?? '#333333';
        const grp = createObject('empty', name);
        name = pathSafe(name, false);
        const card = createObject('cube', name + '_BG');
        setScale(card, w, h, 0.08);
        setColor(card, bg);
        setParent(card, grp);
        if (opts?.title) {
            const titleId = createObject('empty', name + '_Title');
            addBehavior(titleId, 'Text', { text: opts.title, color: tc, fontSize: 12 });
            move(titleId, 0, h * 0.3, 0.06);
            setParent(titleId, grp);
        }
        if (opts?.body) {
            const bodyId = createObject('empty', name + '_Body');
            addBehavior(bodyId, 'Text', { text: opts.body, color: tc, fontSize: 7 });
            move(bodyId, 0, -h * 0.1, 0.06);
            setParent(bodyId, grp);
        }
        return grp;
    }
    function createButton(name: string, label: string, opts?: {
        color?: string;
        textColor?: string;
        width?: number;
        height?: number;
        fontSize?: number;
    }): string {
        const w = opts?.width ?? 1.5;
        const h = opts?.height ?? 0.5;
        // Depth, corners and label follow the face, so a tabletop button is a
        // thin rounded key, not a deep cube under a giant label. The label's
        // em (fontSize 100 = 1 m) is 60% of the height, narrowed until the
        // label fits the width at ~0.7 em per character. The default fill
        // keeps the white label above 4.5:1 contrast.
        const d = 0.2 * h;
        const fontSize = opts?.fontSize ?? Math.min(60 * h, 140 * w / Math.max(label.length, 1));
        const bg = opts?.color ?? '#2563EB';
        const tc = opts?.textColor ?? '#FFFFFF';
        const grp = createObject('empty', name);
        name = pathSafe(name, false);
        const btn = createObject('cube', name + '_BG');
        setScale(btn, w, h, d);
        setColor(btn, bg);
        addBehavior(btn, 'Bevel', { cornerRadius: 0.3 * h });
        setParent(btn, grp);
        const lbl = createObject('empty', name + '_Label');
        addBehavior(lbl, 'Text', { text: label, color: tc, fontSize, fontWeight: 'bold' });
        move(lbl, 0, 0, 0.6 * d);
        setParent(lbl, grp);
        addBehavior(grp, 'Properties', {
            properties: [],
            states: BUTTON_STATES,
        });
        addBehavior(grp, 'BoxCollider', { size: [w, h, d] });
        return grp;
    }
    interface StackOpts {
        direction?: 'x' | 'y' | 'z';
        gap?: number;
        padding?: number;
        width?: number;
        height?: number;
        depth?: number;
        align?: 'Start' | 'Center' | 'End' | 'Stretch';
        justify?: 'Start' | 'Center' | 'End';
    }
    const DIR_MAP = { x: 'X', y: 'Y', z: 'Z' } as const;
    function createStack(name?: string, opts?: StackOpts): string {
        const id = createObject('empty', name ?? 'Stack');
        const pad = opts?.padding ?? 0;
        addBehavior(id, 'Layout', {
            direction: DIR_MAP[opts?.direction ?? 'y'],
            gap: opts?.gap ?? 0.02,
            // A flat stack pads its face only. Padding its 1 cm depth as well
            // would put the content `pad - depth / 2` from the face: inside a
            // card's slab when the padding is small, floating off it when large.
            padding: opts?.direction === 'z' ? pad : { left: pad, right: pad, top: pad, bottom: pad },
            alignMain: opts?.justify ?? 'Start',
            alignCross: opts?.align ?? 'Center',
            alignCrossB: 'End',
            width: opts?.width, widthMode: opts?.width !== undefined ? 'Fixed' : 'Hug',
            height: opts?.height, heightMode: opts?.height !== undefined ? 'Fixed' : 'Hug',
            depth: opts?.depth ?? 0.01, depthMode: 'Fixed',
        });
        return id;
    }
    let backgroundCount = 0;
    function createBackground(target: string, opts?: {
        color?: string;
        cornerRadius?: number;
        pill?: boolean;
        opacity?: number;
        depth?: number;
        name?: string;
        radius?: number;
    }, { surface = false }: { surface?: boolean } = {}): string {
        const body = createObject('cube', opts?.name ?? 'Background');
        setParent(body, target);
        move(body, 0, 0, Math.min(-0.004 + 0.0004 * backgroundCount++, -0.0015));
        addBehavior(body, 'LayoutItem', {
            positionMode: 'Absolute',
            widthMode: 'Fill', heightMode: 'Fill',
            depthMode: 'Fixed', depth: opts?.depth ?? 0.006,
        });
        let cornerRadius = opts?.cornerRadius;
        if (opts?.radius !== undefined) {
            warnings.push(bevelRadiusUnitWarning(opts.name ?? 'Background'));
            cornerRadius ??= legacyBevelRadiusMetres(opts.radius) ?? 0;
        }
        addBehavior(body, 'Bevel', {
            cornerRadius: cornerRadius ?? 0.012,
            ...(opts?.pill ? { pill: true } : {}),
            ...(surface ? { edges: 'all' as const } : {}),
        });
        const color = opts?.color ?? '#F7F6F3';
        // A translucent (glass) card shows what is behind it, so it gets no lift.
        const lift = surface && opts?.opacity === undefined ? surfaceLift(color) : undefined;
        addBehavior(body, 'Renderer', {
            materials: [{ targetMaterialIndex: 0, baseColorFactor: color, ...(lift ? { emissiveFactor: lift } : {}) }],
            ...(opts?.opacity !== undefined ? { opacity: opts.opacity } : {}),
        });
        return body;
    }
    function createSurfaceCard(name?: string, opts?: StackOpts & {
        color?: string;
        cornerRadius?: number;
        pill?: boolean;
        opacity?: number;
        radius?: number;
    }): string {
        const id = createStack(name ?? 'Card', { padding: 0.04, gap: 0.02, ...opts });
        findObj(id)!.card = true;
        // A card of known size rounds in proportion to it; a hugging card rounds
        // by its padding, so content keeps clear of the curve. The rounded rim
        // of its 6 mm body shades into a hairline edge.
        const side = opts?.width !== undefined && opts?.height !== undefined ? Math.min(opts.width, opts.height) : undefined;
        const padding = opts?.padding ?? 0.04;
        createBackground(id, {
            name: `${pathSafe(name ?? 'Card', false)} Body`,
            color: opts?.color ?? '#FFFFFF',
            // A legacy `radius` is read by createBackground; the size default applies only without it.
            cornerRadius: opts?.cornerRadius ?? (opts?.radius !== undefined ? undefined : side !== undefined ? 0.05 * side : Math.max(0.004, 0.75 * padding)),
            ...(opts?.radius !== undefined ? { radius: opts.radius } : {}),
            ...(opts?.pill ? { pill: true } : {}),
            ...(opts?.opacity !== undefined ? { opacity: opts.opacity } : {}),
        }, { surface: true });
        return id;
    }
    function createLabel(content: string, opts?: {
        fontSize?: number;
        color?: string;
        bold?: boolean;
        name?: string;
        letterSpacing?: number;
        maxWidth?: number;
        step?: string | number;
    }): string {
        if (opts?.step !== undefined && opts.step !== null && opts.step !== '')
            return createStepLabel(content, String(opts.step), opts);
        const id = createObject('empty', opts?.name ?? freeName(pathSafe(`Label ${content.slice(0, 40).trimEnd()}`, false)));
        // Same rule as `LayoutItem.hugCeiling` (behaviors/layoutitem.ts); this layer imports behavior types only.
        const width = opts?.maxWidth !== undefined && Number.isFinite(opts.maxWidth) && opts.maxWidth > 0
            ? opts.maxWidth : undefined;
        addBehavior(id, 'LayoutItem', {
            widthMode: 'Hug',
            ...(width !== undefined ? { maxWidth: width } : {}),
            heightMode: 'Hug', depthMode: 'Fixed', depth: 0.001,
        });
        const overrides: SpatialOverrides<'Text'> = {
            text: content,
            fontSize: opts?.fontSize ?? 5,
            color: opts?.color ?? '#1A1A1A',
        };
        if (opts?.bold)
            overrides.fontWeight = 'bold';
        if (opts?.letterSpacing !== undefined)
            overrides.letterSpacing = opts.letterSpacing;
        addBehavior(id, 'Text', overrides);
        return id;
    }
    /**
     * A caption led by its step number in a badge: a group at the given
     * position holding a pill with the number and, beside it, the text pinned
     * top-left with a hanging indent. Text and LayoutItem calls on the returned
     * handle reach the text. It is placed with move(), not flowed: the group has
     * no LayoutItem of its own, so a stack() or card() would give it no size.
     */
    function createStepLabel(content: string, step: string, opts: NonNullable<Parameters<typeof createLabel>[1]>): string {
        const fontSize = opts.fontSize ?? 5;
        const em = fontSize / 100;
        const name = opts.name ?? freeName(pathSafe(`Label ${step}  ${content.slice(0, 36).trimEnd()}`, false));
        const group = createObject('empty', name);
        const safe = pathSafe(name, false);
        // The pill is a line tall and wide enough for the number, centred on the first line.
        const h = 1.3 * em, w = Math.max(h, (0.47 * step.length + 0.65) * em), d = 0.08 * h;
        const indent = w + 0.4 * em;
        const badge = createObject('cube', `${safe} Badge`);
        setParent(badge, group);
        setScale(badge, w, h, d);
        move(badge, w / 2, -0.6 * em, -d / 2 - 0.0005);
        addBehavior(badge, 'Bevel', { pill: true, edges: 'all' });
        const fill = badgeColor(opts.color);
        addBehavior(badge, 'Renderer', { materials: [{ targetMaterialIndex: 0, baseColorFactor: fill, emissiveFactor: surfaceLift(fill) ?? undefined }] });
        const number = createLabel(step, { fontSize: 0.78 * fontSize, color: '#FFFFFF', bold: true, name: `${safe} Number` });
        setParent(number, group);
        move(number, w / 2, -0.6 * em, 0.0005);
        // The text wraps in what the badge leaves of maxWidth, at least one em.
        const maxWidth = opts.maxWidth !== undefined && opts.maxWidth > 0 ? Math.max(opts.maxWidth - indent, em) : undefined;
        const text = createLabel(content, { ...opts, step: undefined, name: `${safe} Text`, maxWidth });
        setParent(text, group);
        move(text, indent, 0, 0);
        addBehavior(text, 'Text', { anchorX: 'left', anchorY: 'top' });
        textRedirects.set(group, text);
        return group;
    }
    function createDeclaredImage(prompt: string, width?: number | string, height?: number, name?: string): string {
        if (!prompt || typeof prompt !== 'string') {
            throw new Error('image() requires a generation prompt (string).');
        }
        if (typeof width === 'string') {
            name = width;
            width = undefined;
        }
        const w = typeof width === 'number' && width > 0 ? width : undefined;
        const h = typeof height === 'number' && height > 0 ? height : undefined;
        const id = createObject('empty', name ?? freeName(pathSafe(`Image ${prompt.slice(0, 24)}`, false)));
        const desc = findObj(id)!;
        desc.imagePrompt = prompt;
        if (w !== undefined && h !== undefined)
            desc.scale = [w, h, 1];
        addBehavior(id, 'Image', {});
        addBehavior(id, 'LayoutItem', {
            ...(w !== undefined ? { width: w, widthMode: 'Fixed' } : { widthMode: 'Fill' }),
            ...(h !== undefined ? { height: h, heightMode: 'Fixed' } : { heightMode: 'Fill' }),
            depthMode: 'Fixed', depth: 0.005,
            flexShrink: 0,
        });
        return id;
    }
    /** A snapshot the build code reads but can't change. */
    function readOnly(o: SpatialSceneSnapshot): SpatialSceneSnapshot {
        const frozen = <T extends unknown[]>(v: T | undefined): T | undefined => v && Object.freeze([...v]) as unknown as T;
        return Object.freeze({
            ...o,
            position: frozen(o.position)!, rotation: frozen(o.rotation)!, scale: frozen(o.scale)!,
            dimensions: frozen(o.dimensions),
            bounds: o.bounds && Object.freeze({ min: frozen(o.bounds.min)!, max: frozen(o.bounds.max)!, center: frozen(o.bounds.center)! }),
            behaviors: frozen(o.behaviors)!,
        });
    }
    function getObject(name: string): SpatialSceneSnapshot | null {
        if (!existingScene)
            return null;
        const byPath = name.includes('/') ? (existingScene.find(o => o.path === name)
            ?? existingScene.find(o => o.path === `Scene/${name}`)) : undefined;
        const matches = byPath ? [byPath] : existingScene.filter(o => o.name === name);
        if (matches.length > 1)
            throw new Error(`getObject(): name "${name}" is ambiguous. Use a full path: ${matches.map(o => o.path).join(', ')}`);
        const found = matches[0];
        return found ? readOnly(found) : null;
    }
    function listObjects(): SpatialSceneSnapshot[] {
        return existingScene ? existingScene.map(readOnly) : [];
    }
    const blocks = createBlockTools({
        create: createObject,
        find: findObj,
        objects: () => objects,
        behavior: (id, type, overrides) => addBehavior(id, type as SpatialBehaviorType, overrides as SpatialOverrides<SpatialBehaviorType>),
        parent: setParent,
        background: (target, opts) => createBackground(target, opts),
        redirect: (from, to) => { parentRedirects.set(from, to); },
        interpose: (child, name) => {
            const desc = findObj(child);
            if (!desc?.parentId) return undefined;
            reserve(1);
            const id = `__sp_${idCounter++}`;
            const at = objects.indexOf(desc);
            objects.splice(at, 0, { id, name: pathSafe(name), template: 'empty', parentId: desc.parentId });
            desc.parentId = id;
            return id;
        },
        interaction: source => interaction(source),
        warn: message => { warnings.push(message); },
        color: setColor,
    }, { style: options?.style, fonts: options?.fonts });
    const calloutRoots = new Set<string>();
    function createCallouts(subject: string, items: readonly CalloutItem[], opts?: CalloutOptions): string {
        const id = idOf(subject);
        return buildCallouts({
            find: findObj, objects: () => objects, create: (template, name) => createObject(template, name),
            freeName: base => freeName(pathSafe(base, false)), parent: setParent, move, rotate, scale: setScale,
            behavior: (obj, type, overrides) => addBehavior(obj, type as SpatialBehaviorType, overrides as SpatialOverrides<SpatialBehaviorType>),
            card: (name, o) => createSurfaceCard(name, o), label: createLabel, interaction, warn: m => { warnings.push(m); },
            style: options?.style, ignore: calloutRoots,
        }, id, items, opts);
    }
    const FREE_PLACEMENT = ['text', 'stack', 'image', 'makeCard', 'makeButton', 'card', 'background', 'label', 'callouts'];
    const api: Record<string, unknown> = {
        cube: (name?: string, opts?: unknown) => createPrimitive('cube', name, opts),
        sphere: (name?: string, opts?: unknown) => createPrimitive('sphere', name, opts),
        cylinder: (name?: string, opts?: unknown) => createPrimitive('cylinder', name, opts),
        plane: (name?: string, opts?: unknown) => createPrimitive('plane', name, opts),
        group: (name?: string) => createObject('empty', name),
        text: createText, makeCard: createCard, makeButton: createButton, particleEmitter: createParticleEmitter,
        material: setMaterial, environment: setEnvironment, light: setLight,
        stack: createStack, card: createSurfaceCard, background: (target: string, opts?: Parameters<typeof createBackground>[1]) => createBackground(target, opts), label: createLabel, callouts: createCallouts, image: createDeclaredImage,
        asset: createAsset, clone: cloneObject, instances: createInstances,
        move, rotate, scale: setScale, color: setColor, parent: setParent,
        addBehavior, setActive, inspector, bake, seat,
        onClickToggle, onClickShowExclusive, onClickSetState, interaction,
        getObject, listObjects, Math, Vec3: SandboxVec3, MathUtil: SandboxMathUtil, rng,
    };
    let blockErrors: string[] | undefined;
    let finished = false;
    if (options?.surface === 'blocks') {
        for (const name of FREE_PLACEMENT) delete api[name];
        Object.assign(api, blocks.api);
    }
    const result = (errors: string[] = []): SpatialBuildResult => {
        if (!errors.length)
            errors = finishBakedCopies();
        if (!errors.length) {
            const inBake = folded();
            const lost = objects.filter(o => o.seat && inBake.has(o.id));
            if (lost.length)
                errors = [`seat(): ${lost.slice(0, 3).map(o => `"${o.name}"`).join(', ')} ${lost.length === 1 ? 'is' : 'are'} folded into a bake(); seat the baked group instead.`];
        }
        if (errors.length)
            return { objects, interactions, errors, warnings, stats: statsNow(), ...(environment ? { environment } : {}) };
        for (const [source, pick] of interactionPickModes) {
            if (pick === 'geometry' && findObj(source)?.behaviors?.some(b => b.type === 'BoxCollider' || b.type === 'SphereCollider')) {
                return { objects, interactions, errors: ['interaction: geometry picking requires a source without a collider'], warnings, stats: statsNow() };
            }
        }
        if (options?.surface === 'blocks') {
            // Once: finalize writes the sizing it would otherwise judge on a second call.
            blockErrors ??= blocks.finalize();
            if (blockErrors.length)
                return { objects, interactions, errors: blockErrors, warnings, stats: statsNow(), ...(environment ? { environment } : {}) };
            if (!finished) warnings.push(...blocks.notes(), ...finishBuild(objects));
            finished = true;
        }
        for (const desc of objects) {
            if (!desc.parentObjectName || desc.template !== 'empty')
                continue;
            const slot = existingScene?.find(o => o.path === desc.parentObjectName)
                ?? existingScene?.find(o => o.name === desc.parentObjectName);
            if (!slot?.behaviors.includes('Layout'))
                continue;
            if (desc.behaviors?.some(b => b.type === 'Layout' || b.type === 'LayoutItem'))
                continue;
            (desc.behaviors ??= []).push({ type: 'LayoutItem', overrides: {
                    widthMode: 'Fill', heightMode: 'Fill', depthMode: 'Fixed', depth: 0.001, flexShrink: 0,
                } });
            warnings.push(`"${desc.name}" was a plain group inside slot "${slot.name}"; gave it a Fill/Fill LayoutItem so the slot flows it at its face — do not move() a slot fill in z.`);
        }
        for (const desc of objects) {
            if (!desc.imagePrompt || !desc.parentId)
                continue;
            const item = desc.behaviors?.find(b => b.type === 'LayoutItem')?.overrides;
            const parentLayout = findObj(desc.parentId)?.behaviors?.find(b => b.type === 'Layout')?.overrides;
            if (!item || !parentLayout)
                continue;
            const hug = (['width', 'height'] as const)
                .filter(a => item[`${a}Mode`] === 'Fill' && parentLayout[`${a}Mode`] === 'Hug');
            if (hug.length) {
                warnings.push(`"${desc.name}": image() with no ${hug.join('/')} inside a Hug stack collapses to 0 — give the stack a ${hug.join('/')} or size the image.`);
            }
        }
        for (const interaction of interactions) {
            if (interaction.type === 'click_show_exclusive') {
                exclusiveGroups.forEach((groupSet) => {
                    if (groupSet.has(interaction.targetId)) {
                        interaction.exclusiveGroupIds = Array.from(groupSet);
                    }
                });
            }
        }
        try { validateSpatialInteractions(interactions); }
        catch (error) {
            return { objects, interactions, errors: [error instanceof Error ? error.message : String(error)], warnings, stats: statsNow(), environment };
        }
        if (environment)
            warnings.push(`Scene ${describeEnvironment(environment)}.`);
        const given = [...slashed];
        const renamed = given.length
            ? `"/" separates path segments, so these names have "-" instead: ${given.slice(0, 3).map(name => `"${name}"`).join(', ')}${given.length > 3 ? ` and ${given.length - 3} more` : ''}.`
            : undefined;
        if (renamed && !warnings.includes(renamed))
            warnings.push(renamed);
        const collisions = nameCollisionWarning();
        if (collisions && !warnings.includes(collisions))
            warnings.push(collisions);
        return { objects, interactions, errors: [], warnings, stats: statsNow(), environment };
    };
    return { api, result };
}
